// src/pages/GroupsApplicationsList.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import axios from '../api/axios';

import './ApplicationsList.css';
import './GroupsList.css';
import GroupCategoriesInlineEditor from '../components/GroupCategoriesInlineEditor';
import '../components/GroupCategoriesInlineEditor.css';
import GroupGeneralInfoEditor from '../components/GroupGeneralInfoEditor';

import PublisherEditInline from './PublisherEditInline';
import ApplicationViewPublisher from './ApplicationViewPublisher';
import NewApplicationForm from './NewApplicationForm';

import NCALayerModal from '../components/NCALayerModal';
import { ensureNCALayerConnected } from '../utils/ensureNCALayer';

import EditGroupModal from '../components/EditGroupModal';

/** ====== ref endpoints (проверь у себя названия) ====== */
const REF_ENDPOINTS = {
  categories: '/publisher/ref/categories',
  subjects: '/publisher/ref/subjects',
  directions: '/publisher/ref/directions',
  educationLevels: '/publisher/ref/education-levels',
  grades: '/publisher/ref/grades',
  publicationTypes: '/publisher/ref/publication-types',
  serviceTypes: '/publisher/ref/service-types',
  criteriaForms: '/publisher/ref/criteria-forms',
  developmentLanguages: '/publisher/ref/languages',
  teachingLanguages: '/publisher/ref/languages',
  submissionYears: '/publisher/ref/years',
};

/** ====== helper: take name by locale safely ====== */
const byLocaleName = (obj, locale) => {
  if (!obj) return '';
  const key = `name_${locale}`;
  return obj?.[key] ?? obj?.name ?? '';
};

/** ====== helper: normalize categories from backend ======
 * backend:
 * categories: [{id, name_kz, name_ru, pivot: {is_filled: 1/0}}]
 * normalize to: {id, name_kz, name_ru, is_filled:boolean}
 */
const normalizeCategories = (arr) => {
  const list = Array.isArray(arr) ? arr : [];
  return list.map((c) => ({
    ...c,
    is_filled: Boolean(Number(c?.pivot?.is_filled ?? c?.is_filled ?? 0)),
  }));
};

export default function GroupsApplicationsList({ forcedLocale }) {
  const locale = forcedLocale || localStorage.getItem('locale') || 'kz';

  const findById = (arr, id) => (arr || []).find((x) => String(x.id) === String(id)) || null;

  const enrichGroupByRefs = (g) => {
    if (!g) return g;
    return {
      ...g,
      subject: findById(refs.subjects, g.subject_id),
      grade: findById(refs.grades, g.grade_id),
      education_level: findById(refs.educationLevels, g.education_level_id),
      direction: findById(refs.directions, g.direction_id),
      development_language: findById(refs.developmentLanguages, g.development_language_id),
      teaching_language: findById(refs.teachingLanguages, g.teaching_language_id),
      publication_type: findById(refs.publicationTypes, g.publication_type_id),
      service_type: findById(refs.serviceTypes, g.service_type_id),
      submission_year: findById(refs.submissionYears, g.submission_year_id),
    };
  };

  const t = useMemo(
    () =>
      ({
        kz: {
          groupsTitle: 'Құрылған ОӘК және өтінімдер',
          categoriesShort: 'санат',
          notSent: 'Жіберілмеген',
          sent: 'Жіберілді',
          sendGroup: 'Топты жіберу',
          confirmSendTitle: 'Топты жіберу',
          ecpSign: 'ЭЦҚ қол қою',
          confirmSendText: (name) => `«${name}» тобын жібергіңіз келе ме?`,
          emptyGroups: 'Топтар табылмады',
          list: 'Өтінімдер тізімі',
          title: 'Оқу басылымы',
          subject: 'Пән',
          category: 'Санат',
          grade: 'Сынып',
          status: 'Күйі',
          date: 'Күні',
          actions: 'Әрекеттер',
          view: 'Қарау',
          edit: 'Өңдеу',
          remove: 'Жою',
          confirmRemove: 'Бұл өтінішті жоюға сенімдісіз бе?',
          loadErr: 'Тізімді жүктеу мүмкін емес',
          catUnfilled: 'Барлық санат түрлері толтырылуы қажет',
          ungroupped: 'Топсыыыыз Өтінімдер',
          ok: 'ОК',
          cancel: 'Болдырмау',
          resubmit: 'Қайта жіберу',
          resubmitting: 'Жіберілуде...',
          timeLeft: 'Қалған уақыт',
          timeExpired: 'Уақыт аяқталды',
          confirmResubmit: 'Осы өтінімді қайта жібересіз бе?',
          resubmitOk: 'Өтінім сәтті қайта жіберілді',
          resubmitErr: 'Қайта жіберу кезінде қате',
          declined: 'Қабылданбады',
          searchPh: 'Іздеу (атау немесе пән)...',
          sortLabel: 'Сұрыптау',
          perPage: 'Саны',
          sortCreatedAsc: 'Бұрынғылардан бастап',
          sortCreatedDesc: 'Кейінгілерден бастап',
          sortTitleAsc: 'Атауы: A → Я',
          sortTitleDesc: 'Атауы: Я → A',
          reset: 'Таза',
          page: 'Бет',
          of: 'із',
          prev: 'Алдыңғы',
          next: 'Келесі',
          hasProcessingTitle: 'Өңдеудегі Өтінімдер бар',
          signError: 'ЭЦҚ қолтаңбасын жасау кезінде қате',
          signSuccess: 'ОӘК сәтті жіберілді!',
          deleteGroup: 'Топты жою',
          confirmDeleteTitle: 'Топты жою',
          confirmDeleteText: (name) =>
            `«${name}» тобын өшіргіңіз келе ме? Бұл топтағы Өтінімдер топсыз қалады.`,
          deleteOk: 'Топ жойылды',
          deleteErr: 'Топты жою кезінде қате',

          fillApp: 'Өтінімді толтыру',
          modalFillTitle: 'Өтінімді толтыру',

          addCats: 'Санат қосу',
          addCatsTitle: 'Санат қосу',
          addCatsHint: 'Осы ОӘК-ке қосатын санаттарды таңдаңыз',
          addCatsSave: 'Сақтау',
          addCatsCancel: 'Болдырмау',
          addCatsEmpty: 'Қосатын санат жоқ',
          addCatsErr: 'Санаттарды қосу кезінде қате',
          searchCat: 'Санатты іздеу…',

          editGroup: 'Топты өңдеу',
          editGroupTitle: 'Топты өңдеу',
          groupName: 'Топ атауы',
          save: 'Сақтау',
          saveErr: 'Сақтау кезінде қате',

          // edit form labels
          direction: 'Бағыт',
          educationLevel: 'Білім деңгейі',
          gradeLabel: 'Сынып',
          publicationType: 'Басылым түрі',
          serviceType: 'Қызмет түрі',
          criteriaForm: 'Критерий формасы',
          devLang: 'Әзірлеу тілі',
          teachLang: 'Оқыту тілі',
          submissionYear: 'Өткізу жылы',
          categories: 'Санаттар',
          required: 'Міндетті өріс',

          // group info block
          groupInfoTitle: 'Топ туралы жалпы ақпарат',
          groupInfoSubject: 'Пән',
          groupInfoGrade: 'Сынып',
          groupInfoEduLevel: 'Білім беру деңгейі',
          groupInfoDirection: 'Бағыт',
          groupInfoDevLang: 'Әзірлеу тілі',
          groupInfoTeachLang: 'Оқыту тілі',
          groupInfoPubType: 'Басылым түрі',
          groupInfoServiceType: 'Қызмет түрі',
          groupInfoSubmissionYear: 'Өтінім беру жылы',

          addCatsDisabledPreSchool: 'Мектепке дейінгі деңгей үшін санат бекітілген',

          // NEW UX: unified edit
          editAll: 'Өзгерту',
          closeEdit: 'Жабу',
          editAllTitle: 'Өңдеу (санаттар + жалпы ақпарат)',
        },
        ru: {
          groupsTitle: 'Созданные УМК и заявки',
          categoriesShort: 'кат.',
          notSent: 'Не отправлено',
          sent: 'Отправлено',
          sendGroup: 'Отправить группу',
          confirmSendTitle: 'Отправить группу',
          ecpSign: 'Подписать ЭЦП',
          confirmSendText: (name) => `Вы хотите отправить группу «${name}»?`,
          emptyGroups: 'Группы не найдены',
          list: 'Список заявок',
          title: 'Учебное издание',
          subject: 'Предмет',
          category: 'Категория',
          grade: 'Класс',
          status: 'Статус',
          date: 'Дата',
          actions: 'Действия',
          view: 'Просмотр',
          edit: 'Редактировать',
          remove: 'Удалить',
          confirmRemove: 'Вы уверены, что хотите удалить эту заявку?',
          loadErr: 'Не удалось загрузить список',
          catUnfilled: 'Требуется заполнить все категории',
          ungroupped: 'Заявки без группы',
          ok: 'ОК',
          cancel: 'Отмена',
          resubmit: 'Переотправить',
          resubmitting: 'Отправка...',
          timeLeft: 'Осталось',
          timeExpired: 'Время истекло',
          confirmResubmit: 'Переотправить эту заявку?',
          resubmitOk: 'Заявка успешно переотправлена',
          resubmitErr: 'Ошибка при переотправке',
          declined: 'Отклонено',
          searchPh: 'Поиск (по названию или предмету)...',
          sortLabel: 'Сортировка',
          perPage: 'Количество',
          sortCreatedAsc: 'Сначало ранние',
          sortCreatedDesc: 'Сначало новые',
          sortTitleAsc: 'Название: A → Я',
          sortTitleDesc: 'Название: Я → A',
          reset: 'Сброс',
          page: 'Стр.',
          of: 'из',
          prev: 'Назад',
          next: 'Вперёд',
          hasProcessingTitle: 'Есть заявки на доработке',
          signError: 'Ошибка при создании ЭЦП-подписи',
          signSuccess: 'УМК успешно отправлена',
          deleteGroup: 'Удалить группу',
          confirmDeleteTitle: 'Удаление группы',
          confirmDeleteText: (name) =>
            `Удалить группу «${name}»? Заявки из группы останутся без группы.`,
          deleteOk: 'Группа удалена',
          deleteErr: 'Ошибка при удалении группы',

          fillApp: 'Заполнить заявку',
          modalFillTitle: 'Заполнить заявку',

          addCats: 'Добавить категорию',
          addCatsTitle: 'Добавить категории',
          addCatsHint: 'Выберите категории, которые нужно добавить в эту УМК',
          addCatsSave: 'Сохранить',
          addCatsCancel: 'Отмена',
          addCatsEmpty: 'Нет доступных категорий',
          addCatsErr: 'Ошибка при добавлении категорий',
          searchCat: 'Поиск категории…',

          editGroup: 'Редактировать группу',
          editGroupTitle: 'Редактировать группу',
          groupName: 'Название группы',
          save: 'Сохранить',
          saveErr: 'Ошибка при сохранении',

          direction: 'Направление',
          educationLevel: 'Уровень образования',
          gradeLabel: 'Класс',
          publicationType: 'Тип издания',
          serviceType: 'Тип услуги',
          criteriaForm: 'Форма критериев',
          devLang: 'Язык разработки',
          teachLang: 'Язык обучения',
          submissionYear: 'Год подачи',
          categories: 'Категории',
          required: 'Обязательное поле',

          // group info block
          groupInfoTitle: 'Общая информация группы',
          groupInfoSubject: 'Предмет',
          groupInfoGrade: 'Класс',
          groupInfoEduLevel: 'Уровень образования',
          groupInfoDirection: 'Направление',
          groupInfoDevLang: 'Язык разработки',
          groupInfoTeachLang: 'Язык обучения',
          groupInfoPubType: 'Тип издания',
          groupInfoServiceType: 'Тип услуги',
          groupInfoSubmissionYear: 'Год подачи',

          addCatsDisabledPreSchool: 'Для дошкольного уровня категория фиксированная',

          // NEW UX: unified edit
          editAll: 'Изменить',
          closeEdit: 'Закрыть',
          editAllTitle: 'Редактировать (категории + общая информация)',
        },
      })[locale],
    [locale]
  );

  /** =========================
   * helpers
   * ========================= */
  const norm = (v) => String(v || '').trim().toLowerCase();

  const statusKey = (raw) => {
    const s = norm(raw);
    const known = new Set([
      'draft',
      'submitted',
      'accepted',
      'in_review',
      'in_processing',
      'in_expertise',
      'external_experts_assigned',
      'ready_for_contract',
      'sent_to_gov_service',
      'completed',
      'ready_for_order',
    ]);
    if (known.has(s)) return s;

    if (['жоба', 'черновик', 'draft'].some((x) => s.includes(x))) return 'draft';
    if (['жіберілді', 'отправ', 'submitted', 'sent'].some((x) => s.includes(x))) return 'submitted';
    if (['қабылданды', 'принят', 'accepted', 'approved'].some((x) => s.includes(x))) return 'accepted';
    if (['өңдеуде', 'на рассмотр', 'in_review', 'review'].some((x) => s.includes(x))) return 'in_review';
    if (['өңделіп жатыр', 'processing', 'in_processing'].some((x) => s.includes(x))) return 'in_processing';
    if (['сараптамада', 'экспертиз', 'in_expertise'].some((x) => s.includes(x))) return 'in_expertise';
    if (['сыртқы сарапшылар', 'external_experts_assigned'].some((x) => s.includes(x)))
      return 'external_experts_assigned';
    if (['келісімшартқа дайын', 'ready_for_contract'].some((x) => s.includes(x))) return 'ready_for_contract';
    if (['гос-сервиске', 'sent_to_gov_service'].some((x) => s.includes(x))) return 'sent_to_gov_service';
    if (['толық аяқталды', 'completed'].some((x) => s.includes(x))) return 'completed';
    if (['приказға дайын', 'ready_for_order'].some((x) => s.includes(x))) return 'ready_for_order';
    return 'unknown';
  };
  

  const statusLabel = (key) => {
    const kk = {
      draft: 'Жіберілмеген',
      submitted: 'Жіберілді',
      accepted: 'Қабылданды',
      in_review: 'Қаралуда',
      in_processing: 'Өңделіп жатыр',
      in_expertise: 'Сараптамада',
      external_experts_assigned: 'Сыртқы сарапшылар тағайындалды',
      ready_for_contract: 'Келісімшартқа дайын',
      sent_to_gov_service: 'Гос-сервиске жіберілді',
      ready_for_order: 'Приказға дайын',
      completed: 'Толық аяқталды',
      unknown: '—',
    };
    const ru = {
      draft: 'Не отправлено',
      submitted: 'Отправлено',
      accepted: 'Принято',
      in_review: 'На рассмотрении',
      in_processing: 'В обработке',
      in_expertise: 'На экспертизе',
      external_experts_assigned: 'Назначены внешние эксперты',
      ready_for_contract: 'Готово к договору',
      sent_to_gov_service: 'Отправлено в госсервис',
      ready_for_order: 'Готово к приказу',
      completed: 'Полностью завершено',
      unknown: '—',
    };
    return (locale === 'ru' ? ru : kk)[key] ?? (locale === 'ru' ? ru.unknown : kk.unknown);
  };

  const groupStatusClass = (key) => {
    switch (key) {
      case 'draft':
        return 'status-unsent'; // сұр

      case 'submitted':
        return 'status-submitted'; // сары

      case 'accepted':
        return 'status-accepted'; // көк/ақшыл

      case 'in_review':
      case 'in_processing':
        return 'status-processing'; // қызғылт-сары

      case 'in_expertise':
        return 'status-expertise'; // ✅ тек сараптамада жасыл

      case 'external_experts_assigned':
        return 'status-external'; // күлгін

      case 'ready_for_contract':
      case 'sent_to_gov_service':
      case 'ready_for_order':
        return 'status-next'; // көк

      case 'completed':
        return 'status-completed'; // қара/нейтрал

      default:
        return 'status-unknown';
    }
  };
  const isDraftApp = (status) => ['жоба күйінде', 'черновик', 'жоба', 'draft'].includes(norm(status));
  const isOnEditApp = (status) =>
    ['өңдеуде', 'на доработке', 'processing', 'in_review', 'in_processing'].includes(norm(status));

  const [catsEditSignals, setCatsEditSignals] = useState({});

  /** NEW: один режим редактирования для группы */
  const [editingGroupId, setEditingGroupId] = useState(null);

  const getTimeLeft = (expiresAtIso) => {
    if (!expiresAtIso) return { ms: 0, textLong: '', d: 0, h: 0, m: 0, s: 0 };
    const end = new Date(expiresAtIso);
    const ms = end - new Date();
    const units =
      locale === 'ru'
        ? { d: 'д', h: 'ч', m: 'мин', s: 'сек' }
        : { d: 'күн', h: 'сағ', m: 'мин', s: 'сек' };
    if (ms <= 0) return { ms: 0, textLong: t.timeExpired, d: 0, h: 0, m: 0, s: 0 };

    const total = Math.floor(ms / 1000);
    const d = Math.floor(total / 86400);
    const h = Math.floor((total % 86400) / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;

    const textLong =
      d > 0
        ? `${d} ${units.d} ${h} ${units.h} ${m} ${units.m} ${String(s).padStart(2, '0')} ${units.s}`
        : `${h} ${units.h} ${m} ${units.m} ${String(s).padStart(2, '0')} ${units.s}`;

    return { ms, textLong, d, h, m, s };
  };

  const timeLeftClass = (ms) =>
    ms <= 60 * 1000 ? 'tl-badge tl-badge--danger' : ms <= 60 * 60 * 1000 ? 'tl-badge tl-badge--warn' : 'tl-badge tl-badge--ok';

  const fmt = useMemo(() => new Intl.DateTimeFormat(locale === 'kz' ? 'kk-KZ' : 'ru-RU'), [locale]);

  /** =========================
   * state
   * ========================= */
  const [groups, setGroups] = useState([]);
  const [expandedGroupId, setExpanded] = useState(null);
  const [modalSendGroup, setModalSendGroup] = useState(null);
  const [modalDeleteGroup, setModalDeleteGroup] = useState(null);

  const [modal, setModal] = useState({ mode: null, applicationId: null });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [resubmittingId, setResubmittingId] = useState(null);

  const [sendingGroup, setSendingGroup] = useState(false);

  const [modalSubMode, setModalSubMode] = useState('view'); // 'view' | 'edit'
const [modalApp, setModalApp] = useState(null); // храним весь app для проверки статуса

  const [newAppModal, setNewAppModal] = useState({ open: false, groupId: null, categoryId: null });

  const expiredCalledRef = useRef(new Set());
  const mountedRef = useRef(true);

  const [ncaModalOpen, setNcaModalOpen] = useState(false);
  const pendingSendGroupRef = useRef(null);

  // add categories modal
  const [catRefs, setCatRefs] = useState([]);
  const [addCatsModal, setAddCatsModal] = useState({ open: false, group: null });
  const [addCatQ, setAddCatQ] = useState('');
  const [addCatPick, setAddCatPick] = useState([]); // FINAL desired set (existing + new)
  const [addCatsSaving, setAddCatsSaving] = useState(false);

  // query state
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);

  /** ===== refs for edit-group full form ===== */
  const [refs, setRefs] = useState({
    subjects: [],
    directions: [],
    educationLevels: [],
    grades: [],
    publicationTypes: [],
    serviceTypes: [],
    criteriaForms: [],
    developmentLanguages: [],
    teachingLanguages: [],
    submissionYears: [],
  });
  const [refsLoading, setRefsLoading] = useState(false);

  /** ===== edit group modal state (new) ===== */
  const [editGroupOpen, setEditGroupOpen] = useState(false);
  const [editGroupId, setEditGroupId] = useState(null);

  /** =========================
   * debounced search
   * ========================= */
  useEffect(() => {
    const id = setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  /** =========================
   * scroll locks
   * ========================= */
  const lockBodyScroll = (on) => {
    if (!on) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => (document.body.style.overflow = prev || '');
  };

  useEffect(() => {
    if (!modal.mode) return;
    return lockBodyScroll(true);
  }, [modal.mode]);

  useEffect(() => {
    if (!newAppModal.open) return;
    const unlock = lockBodyScroll(true);
    const onKey = (e) => e.key === 'Escape' && setNewAppModal({ open: false, groupId: null, categoryId: null });
    document.addEventListener('keydown', onKey);
    return () => {
      unlock?.();
      document.removeEventListener('keydown', onKey);
    };
  }, [newAppModal.open]);

  useEffect(() => {
    if (!addCatsModal.open) return;
    const unlock = lockBodyScroll(true);
    const onKey = (e) => e.key === 'Escape' && closeAddCats();
    document.addEventListener('keydown', onKey);
    return () => {
      unlock?.();
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addCatsModal.open]);

  /** =========================
   * modal open/close handlers
   * ========================= */
  const openEditModal = useCallback((id) => setModal({ mode: 'edit', applicationId: id }), []);
 const openViewModal = useCallback((app) => {
  setModalSubMode('view');
  setModalApp(app);
  setModal({ mode: 'view', applicationId: app.id });
}, []);

const closeModal = useCallback(() => {
  setModal({ mode: null, applicationId: null });
  setModalApp(null);
  setModalSubMode('view');
}, []);

  const triggerGroupCatsEdit = useCallback((groupId) => {
    const key = String(groupId);
    setCatsEditSignals((prev) => ({
      ...prev,
      [key]: (prev[key] || 0) + 1,
    }));
  }, []);

  /** NEW: единая кнопка редактирования группы (категории + общая информация) */
  const toggleGroupEdit = useCallback(
    (group) => {
      const gid = String(group?.id);
      if (!gid || gid === 'no_group') return;

      // раскрываем если закрыто
      if (String(expandedGroupId) !== gid) setExpanded(gid);

      setEditingGroupId((prev) => {
        const next = String(prev) === gid ? null : gid;

        // если включаем — пинаем редактор категорий
        if (next === gid) {
          requestAnimationFrame(() => triggerGroupCatsEdit(group.id));
        }
        return next;
      });
    },
    [expandedGroupId, triggerGroupCatsEdit]
  );

  /** =========================
   * load categories ref for add-cats chips
   * ========================= */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(REF_ENDPOINTS.categories, { headers: { 'Accept-Language': locale } });
        if (!alive) return;
        const list = Array.isArray(r.data) ? r.data : r.data?.data || [];
        setCatRefs(list);
      } catch (e) {
        if (alive) setCatRefs([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [locale]);

  /** =========================
   * group details
   * ========================= */
  const fetchGroupDetails = useCallback(
    async (groupId) => {
      try {
        const res = await axios.get(`/publisher/groups/${groupId}/categories-status`, {
          headers: { 'Accept-Language': locale },
        });

        const g = res.data?.group || null;
        if (!g) return null;

        const cats = normalizeCategories(g.categories || []);
        return { ...g, categories: cats };
      } catch (err) {
        console.error('fetchGroupDetails error', err);
        return null;
      }
    },
    [locale]
  );



  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const params = { page, per_page: perPage, sort_by: sortBy, sort_dir: sortDir };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (searchDebounced) params.search = searchDebounced;

      const resG = await axios.get('/publisher/groups', { params, headers: { 'Accept-Language': locale } });
      const baseGroups = Array.isArray(resG.data?.data)
        ? resG.data.data
        : Array.isArray(resG.data)
          ? resG.data
          : [];

      const meta = resG.data?.meta || null;

      if (meta) {
        setTotal(meta.total ?? baseGroups.length);
        setLastPage(meta.last_page ?? 1);
      } else {
        const totalHdr = Number(resG.headers?.['x-total-count'] || 0);
        setTotal(totalHdr || baseGroups.length);
        setLastPage(Math.max(1, Math.ceil((totalHdr || baseGroups.length) / perPage)));
      }

      const detailedGroups = await Promise.all(
        baseGroups.map(async (g) => {
          const detail = await fetchGroupDetails(g.id);
          const categories = normalizeCategories(detail?.categories || g?.categories || []);
          const isFilledAll = categories.length > 0 && categories.every((c) => c.is_filled);
          return { ...g, ...(detail || {}), categories, isFilledAll };
        })
      );

      let apps = [];
      try {
        const resA = await axios.get('/publisher/applications', { headers: { 'Accept-Language': locale } });
        apps = Array.isArray(resA.data?.data) ? resA.data.data : Array.isArray(resA.data) ? resA.data : [];
      } catch (e) {
        apps = [];
      }

      const byGroup = new Map();
      detailedGroups.forEach((g) => byGroup.set(String(g.id), { ...g, apps: [] }));

      const ungroupped = {
        id: 'no_group',
        title: t.ungroupped,
        status: 'draft',
        categories: [],
        isFilledAll: false,
        apps: [],
      };

      apps.forEach((app) => {
        if (app.group_id == null) return; // ❌ не показываем "Топсыз Өтінімдер"
        const key = String(app.group_id);
        if (byGroup.has(key)) byGroup.get(key).apps.push(app);
      });

      const final = Array.from(byGroup.values());
      if (ungroupped.apps.length) final.push(ungroupped);

      if (mountedRef.current) setGroups(final);
    } catch (e) {
      console.error('fetchAll error:', e);
      if (mountedRef.current) setLoadError(t.loadErr);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [page, perPage, sortBy, sortDir, statusFilter, searchDebounced, fetchGroupDetails, locale, t.ungroupped, t.loadErr]);

  useEffect(() => {
    mountedRef.current = true;
    fetchAll();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchAll]);

  /** =========================
   * auto expire edit window
   * ========================= */
  const markAsDeclinedLocal = useCallback(
    (applicationId) => {
      setGroups((prev) =>
        prev.map((gr) => ({
          ...gr,
          apps: (gr.apps || []).map((a) =>
            a.id === applicationId
              ? { ...a, status: locale === 'ru' ? 'Отклонено' : t.declined, can_resubmit: false }
              : a
          ),
        }))
      );
    },
    [locale, t.declined]
  );

  useEffect(() => {
    // Пока открыто редактирование группы (или любые модалки) — не тикаем,
    // иначе нативный <select> будет закрываться от ререндера.
    if (modal.mode || editingGroupId || editGroupOpen || newAppModal.open || addCatsModal.open) return;

    const id = setInterval(() => {
      setGroups((prev) => {
        let changed = false;

        const next = prev.map((gr) => {
          const nextApps = (gr.apps || []).map((app) => {
            if (!app?.can_resubmit || !app?.edit_window_expires_at) return app;

            const { ms } = getTimeLeft(app.edit_window_expires_at);
            if (ms > 0) return app;

            if (!expiredCalledRef.current.has(app.id)) {
              expiredCalledRef.current.add(app.id);
              changed = true;

              axios.post(`/publisher/applications/${app.id}/auto-decline`).catch((err) =>
                console.warn('auto-decline failed:', err)
              );

              return {
                ...app,
                status: locale === 'ru' ? 'Отклонено' : t.declined,
                can_resubmit: false,
              };
            }

            return app;
          });

          const sameApps =
            nextApps.length === (gr.apps || []).length &&
            nextApps.every((a, i) => a === (gr.apps || [])[i]);

          return sameApps ? gr : { ...gr, apps: nextApps };
        });

        return changed ? next : prev;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [modal.mode, editingGroupId, editGroupOpen, newAppModal.open, addCatsModal.open, locale, t.declined, markAsDeclinedLocal]);

  /** =========================
   * app actions
   * ========================= */
  const statusClassApp = (status) => {
    const s = norm(status);
    if (['completed', 'заверш', 'аяқтал'].some((v) => s.includes(v))) return 'badge badge--neutral';
    if (['сараптамаға қаралуда', 'сараптамада', 'review', 'in_review', 'на рассмотр', 'қаралуда'].some((v) => s.includes(v)))
      return 'badge badge--review';
    if (['submitted', 'sent', 'жіберілді', 'отправ'].some((v) => s.includes(v))) return 'badge badge--sent';
    if (['сараптамаға қабылданды', 'accepted', 'approved', 'қабылданды', 'принят', 'одобр'].some((v) => s.includes(v)))
      return 'badge badge--ok';
    if (['сараптамаға қабылданбады', 'rejected', 'declined', 'отклон', 'қабылданбады', 'қайтарылды'].some((v) => s.includes(v)))
      return 'badge badge--danger';
    if (['processing', 'in_processing', 'на доработ', 'өңдеуде', 'өңделіп жатыр'].some((v) => s.includes(v)))
      return 'badge badge--warn';
    return 'badge badge--neutral';
  };



  const handleResubmitApp = useCallback(
    async (appId) => {
      if (!window.confirm(t.confirmResubmit)) return;
      try {
        setResubmittingId(appId);
        await axios.post(
          `/publisher/applications/${appId}/resubmit`,
          {},
          { headers: { 'Accept-Language': locale } }
        );
        alert(t.resubmitOk);
        await fetchAll();
      } catch (e) {
        console.error('resubmit error:', e);
        alert(t.resubmitErr);
      } finally {
        setResubmittingId(null);
      }
    },
    [fetchAll, locale, t.confirmResubmit, t.resubmitOk, t.resubmitErr]
  );

  /** =========================
   * add categories modal
   * ========================= */
  const openAddCats = (group) => {
    setAddCatQ('');
    const existingIds = normalizeCategories(group?.categories || []).map((c) => String(c.id));
    setAddCatPick(existingIds);
    setAddCatsModal({ open: true, group });
  };

  const closeAddCats = () => {
    setAddCatsModal({ open: false, group: null });
    setAddCatQ('');
    setAddCatPick([]);
    setAddCatsSaving(false);
  };

  const togglePickCat = (id) => {
    const v = String(id);
    setAddCatPick((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  };

  const refreshOneGroup = useCallback(
    async (groupId) => {
      const detail = await fetchGroupDetails(groupId);
      if (!detail) return;

      const categories = normalizeCategories(detail.categories || []);
      const isFilledAll = categories.length > 0 && categories.every((c) => c.is_filled);

      setGroups((prev) =>
        prev.map((g) => {
          if (String(g.id) !== String(groupId)) return g;

          const merged = {
            ...g,
            ...detail,
            apps: g.apps,
            categories,
            isFilledAll,
          };

          return enrichGroupByRefs(merged);
        })
      );
    },
    [fetchGroupDetails, refs]
  );

  // ✅ ВОТ СЮДА СРАЗУ ВСТАВЬ handleDeleteApp
  const handleDeleteApp = useCallback(
    async (appId, groupId) => {
      if (!window.confirm(t.confirmRemove)) return;
      try {
        await axios.delete(`/publisher/applications/${appId}`, { headers: { 'Accept-Language': locale } });

        setGroups((prev) =>
          prev.map((g) => ({
            ...g,
            apps: (g.apps || []).filter((a) => a.id !== appId),
          }))
        );

        if (groupId && String(groupId) !== 'no_group') {
          await refreshOneGroup(groupId);
        }
      } catch (err) {
        console.error('delete app error', err);
        alert('Delete error');
      }
    },
    [t.confirmRemove, locale, refreshOneGroup]
  );

  const saveAddCats = async () => {
    const group = addCatsModal.group;
    if (!group) return;

    const existing = new Set(normalizeCategories(group.categories || []).map((c) => String(c.id)));
    const desired = new Set(addCatPick.map(String));

    const toAdd = [...desired].filter((id) => !existing.has(id)).map((x) => parseInt(x, 10)).filter(Boolean);
    const toRemove = [...existing].filter((id) => !desired.has(id)).map((x) => parseInt(x, 10)).filter(Boolean);

    if (toAdd.length === 0 && toRemove.length === 0) {
      closeAddCats();
      return;
    }

    try {
      setAddCatsSaving(true);

      if (toAdd.length) {
        await axios.post(
          `/publisher/groups/${group.id}/categories/add`,
          { category_ids: toAdd },
          { headers: { 'Accept-Language': locale, 'Content-Type': 'application/json' } }
        );
      }

      if (toRemove.length) {
        await axios.post(
          `/publisher/groups/${group.id}/categories/remove`,
          { category_ids: toRemove },
          { headers: { 'Accept-Language': locale, 'Content-Type': 'application/json' } }
        );
      }

      await refreshOneGroup(group.id);
      closeAddCats();
    } catch (e) {
      console.error('save categories error', e);
      alert(t.addCatsErr);
      setAddCatsSaving(false);
    }
  };

  /** =========================
   * NCA send group
   * ========================= */
  const toBase64Utf8 = (obj) => {
    const json = JSON.stringify(obj);
    const bytes = new TextEncoder().encode(json);
    let binary = '';
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    return btoa(binary);
  };

const handleSendGroup = useCallback(
  async (groupId) => {
    setSendingGroup(true);   // ← добавили
    try {
      const ok = await window.NCA.connect();
      if (!ok) {
        pendingSendGroupRef.current = groupId;
        setNcaModalOpen(true);
        setSendingGroup(false);   // ← добавили
        return;
      }

      const dataToSign = toBase64Utf8({ groupId, ts: Date.now() });

      window.NCA.createCMSSignatureFromBase64('PKCS12', 'SIGNATURE', dataToSign, true, async (res) => {
        const cmsSignature = res?.getResponseObject?.() ?? res?.responseObject;

        if (!cmsSignature || typeof cmsSignature !== 'string') {
          console.error('Bad CMS signature:', cmsSignature);
          alert(t.signError);
          setSendingGroup(false);   // ← добавили
          return;
        }

        try {
          await axios.post(
            `/publisher/groups/${groupId}/send`,
            { signed_data: dataToSign, cms_signature: cmsSignature },
            { headers: { 'Accept-Language': locale, 'Content-Type': 'application/json' } }
          );
        } catch (e) {
          console.error('SEND error:', e?.response?.status, e?.response?.data);
          alert(e?.response?.data?.message || t.signError);
          setSendingGroup(false);   // ← добавили
          return;
        }

        setGroups((prev) =>
          prev.map((g) => (String(g.id) === String(groupId) ? { ...g, status: 'submitted' } : g))
        );
        setModalSendGroup(null);
        setSendingGroup(false);   // ← добавили
        alert(t.signSuccess);
      });
    } catch (e) {
      console.error('Sign error full:', e);
      alert(e?.response?.data?.message || t.signError);
      setSendingGroup(false);   // ← добавили
    }
  },
  [locale, t.signError, t.signSuccess]
);

  /** =========================
   * delete group
   * ========================= */
  const handleDeleteGroup = useCallback(
    async (groupId) => {
      try {
        await axios.delete(`/publisher/groups/${groupId}`, { headers: { 'Accept-Language': locale } });
        setModalDeleteGroup(null);
        alert(t.deleteOk);
        await fetchAll();
      } catch (err) {
        console.error('Delete group error:', err);
        alert(t.deleteErr);
      }
    },
    [fetchAll, locale, t.deleteOk, t.deleteErr]
  );

  /** =========================
   * filters helpers
   * ========================= */
  const resetFilters = () => {
    setPage(1);
    setPerPage(10);
    setStatusFilter('all');
    setSearch('');
    setSearchDebounced('');
    setSortBy('created_at');
    setSortDir('desc');
  };

  const countFilled = (categories) => {
    const cats = normalizeCategories(categories || []);
    const total = cats.length;
    const filled = cats.filter((c) => c.is_filled).length;
    return { filled, total, label: `${filled}/${total}` };
  };

  /** =========================
   * EDIT GROUP (refs + open/close)
   * ========================= */
  const loadAllRefs = useCallback(async () => {
    setRefsLoading(true);
    try {
      const headers = { 'Accept-Language': locale };

      const [
        subjects,
        directions,
        educationLevels,
        grades,
        publicationTypes,
        serviceTypes,
        criteriaForms,
        developmentLanguages,
        teachingLanguages,
        submissionYears,
      ] = await Promise.all([
        axios.get(REF_ENDPOINTS.subjects, { headers }).catch(() => ({ data: [] })),
        axios.get(REF_ENDPOINTS.directions, { headers }).catch(() => ({ data: [] })),
        axios.get(REF_ENDPOINTS.educationLevels, { headers }).catch(() => ({ data: [] })),
        axios.get(REF_ENDPOINTS.grades, { headers }).catch(() => ({ data: [] })),
        axios.get(REF_ENDPOINTS.publicationTypes, { headers }).catch(() => ({ data: [] })),
        axios.get(REF_ENDPOINTS.serviceTypes, { headers }).catch(() => ({ data: [] })),
        axios.get(REF_ENDPOINTS.criteriaForms, { headers }).catch(() => ({ data: [] })),
        axios.get(REF_ENDPOINTS.developmentLanguages, { headers }).catch(() => ({ data: [] })),
        axios.get(REF_ENDPOINTS.teachingLanguages, { headers }).catch(() => ({ data: [] })),
        axios.get(REF_ENDPOINTS.submissionYears, { headers }).catch(() => ({ data: [] })),
      ]);

      const pickArr = (r) => (Array.isArray(r?.data) ? r.data : Array.isArray(r?.data?.data) ? r.data.data : []);
      setRefs({
        subjects: pickArr(subjects),
        directions: pickArr(directions),
        educationLevels: pickArr(educationLevels),
        grades: pickArr(grades),
        publicationTypes: pickArr(publicationTypes),
        serviceTypes: pickArr(serviceTypes),
        criteriaForms: pickArr(criteriaForms),
        developmentLanguages: pickArr(developmentLanguages),
        teachingLanguages: pickArr(teachingLanguages),
        submissionYears: pickArr(submissionYears),
      });
    } finally {
      setRefsLoading(false);
    }
  }, [locale]);

  const openEditGroup = (group) => {
    if (!group?.id || String(group.id) === 'no_group') return;
    setEditGroupId(group.id);
    setEditGroupOpen(true);
  };

  const closeEditGroup = () => {
    setEditGroupOpen(false);
    setEditGroupId(null);
  };

  /** =========================
   * render group info
   * ========================= */
  const renderGroupInfo = (group) => {
    if (!group || String(group.id) === 'no_group') return null;

    const isEditingInfo = String(editingGroupId) === String(group.id);
    const gKey = statusKey(group.status);
    const canEditInfo = gKey === 'draft';

    return (
      <div className="gl-ginfo">
        <div className="gl-ginfo__head">
          <div className="gl-ginfo__title">{t.groupInfoTitle}</div>

          {/* ВАЖНО: отдельную кнопку тут убрали, чтобы было "одна кнопка" в шапке группы */}
          {!canEditInfo ? null : null}
        </div>

        {!isEditingInfo ? (
          <div className="gl-ginfo__grid">
            <div className="gl-ginfo__item">
              <div className="gl-ginfo__label">{t.groupInfoSubject}</div>
              <div className="gl-ginfo__value">{byLocaleName(group.subject, locale) || '—'}</div>
            </div>

            <div className="gl-ginfo__item">
              <div className="gl-ginfo__label">{t.groupInfoGrade}</div>
              <div className="gl-ginfo__value">{byLocaleName(group.grade, locale) || group.grade || '—'}</div>
            </div>

            <div className="gl-ginfo__item">
              <div className="gl-ginfo__label">{t.groupInfoEduLevel}</div>
              <div className="gl-ginfo__value">{byLocaleName(group.education_level, locale) || '—'}</div>
            </div>

            <div className="gl-ginfo__item">
              <div className="gl-ginfo__label">{t.groupInfoDirection}</div>
              <div className="gl-ginfo__value">{byLocaleName(group.direction, locale) || '—'}</div>
            </div>

            <div className="gl-ginfo__item">
              <div className="gl-ginfo__label">{t.groupInfoDevLang}</div>
              <div className="gl-ginfo__value">{byLocaleName(group.development_language, locale) || '—'}</div>
            </div>

            <div className="gl-ginfo__item">
              <div className="gl-ginfo__label">{t.groupInfoTeachLang}</div>
              <div className="gl-ginfo__value">{byLocaleName(group.teaching_language, locale) || '—'}</div>
            </div>

            <div className="gl-ginfo__item">
              <div className="gl-ginfo__label">{t.groupInfoPubType}</div>
              <div className="gl-ginfo__value">{byLocaleName(group.publication_type, locale) || '—'}</div>
            </div>

            <div className="gl-ginfo__item">
              <div className="gl-ginfo__label">{t.groupInfoServiceType}</div>
              <div className="gl-ginfo__value">{byLocaleName(group.service_type, locale) || '—'}</div>
            </div>

            <div className="gl-ginfo__item">
              <div className="gl-ginfo__label">{t.groupInfoSubmissionYear}</div>
              <div className="gl-ginfo__value">{byLocaleName(group.submission_year, locale) || '—'}</div>
            </div>
          </div>
        ) : (
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <GroupGeneralInfoEditor
              open={isEditingInfo}
              group={group}
              locale={locale}
              t={t}
              axios={axios}
              refs={refs}
              refsLoading={refsLoading}
              loadAllRefs={loadAllRefs}
              onClose={() => setEditingGroupId(null)}
              onSaved={async (updatedFromApi, payload) => {
                setGroups((prev) =>
                  prev.map((g) => {
                    if (String(g.id) !== String(group.id)) return g;

                    if (updatedFromApi && typeof updatedFromApi === 'object') {
                      return { ...g, ...updatedFromApi };
                    }

                    return {
                      ...g,
                      ...payload,
                      subject: findById(refs.subjects, payload.subject_id),
                      grade: findById(refs.grades, payload.grade_id),
                      education_level: findById(refs.educationLevels, payload.education_level_id),
                      direction: findById(refs.directions, payload.direction_id),
                      development_language: findById(refs.developmentLanguages, payload.development_language_id),
                      teaching_language: findById(refs.teachingLanguages, payload.teaching_language_id),
                      publication_type: findById(refs.publicationTypes, payload.publication_type_id),
                      service_type: findById(refs.serviceTypes, payload.service_type_id),
                      submission_year: findById(refs.submissionYears, payload.submission_year_id),
                    };
                  })
                );

                setEditingGroupId(null);
              }}
            />
          </div>
        )}
      </div>
    );
  };

  /** =========================
   * render apps table
   * ========================= */
  const renderAppsTable = (group) => {
  if (!group.apps || group.apps.length === 0) return null;

  return (
    <div className="apps-group__body">
      <table className="apps-table">
        <thead>
          <tr>
            <th>{t.category}</th>
            <th>{t.status}</th>
            <th>{t.date}</th>
            <th>{t.timeLeft}</th>
            <th>{t.actions}</th>
          </tr>
        </thead>
        <tbody>
          {group.apps.map((app) => {
            const createdStr = app.created_at ? fmt.format(new Date(app.created_at)) : '';
            return (
              <tr key={app.id}>
                <td data-label={t.category}>
                  {typeof app.category === 'object'
                    ? app.category?.[`name_${locale}`] || app.category?.name || ''
                    : app.category || ''}
                </td>

                <td data-label={t.status}>
                  <span className={statusClassApp(app.status)}>{String(app.status || '')}</span>
                </td>

                <td data-label={t.date}>{createdStr}</td>

                <td data-label={t.timeLeft}>
                  {(() => {
                    if (!app.can_resubmit || !app.edit_window_expires_at) return '-';
                    const { ms, textLong } = getTimeLeft(app.edit_window_expires_at);
                    if (ms <= 0) return '-';
                    const title =
                      locale === 'ru'
                        ? `Осталось: ${textLong}. До: ${new Date(app.edit_window_expires_at).toLocaleString()}`
                        : `Қалған уақыт: ${textLong}. Дейін: ${new Date(app.edit_window_expires_at).toLocaleString()}`;
                    return (
                      <span className={timeLeftClass(ms)} title={title}>
                        ⏳ {textLong}
                      </span>
                    );
                  })()}
                </td>

                <td data-label={t.actions}>
                  <div className="apps-actions">
                    {/* 👁 Просмотр — единственная кнопка входа в модалку */}
                    <button className="btn btn--view btn--icon" onClick={() => openViewModal(app)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                      {t.view}
                    </button>

                    {/* 🔄 Қайта жіберу */}
                    {app.can_resubmit &&
                      (() => {
                        const { ms } = getTimeLeft(app.edit_window_expires_at);
                        return (
                          <button
                            className="btn btn--success btn--icon"
                            onClick={() => handleResubmitApp(app.id)}
                            disabled={resubmittingId === app.id || ms <= 0}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="1 4 1 10 7 10"/>
                              <path d="M3.51 15a9 9 0 1 0 .49-3.5"/>
                            </svg>
                            {resubmittingId === app.id ? t.resubmitting : t.resubmit}
                          </button>
                        );
                      })()}

                    {/* 🗑 Жою — только для черновиков */}
                    {isDraftApp(app.status) && (
                      <button
                        className="btn btn--danger btn--icon"
                        onClick={() => handleDeleteApp(app.id, group.id)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14H6L5 6"/>
                          <path d="M10 11v6M14 11v6"/>
                          <path d="M9 6V4h6v2"/>
                        </svg>
                        {t.remove}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

  /** =========================
   * pagination
   * ========================= */
  const canPrev = page > 1;
  const canNext = page < lastPage;
  const gotoPage = (p) => setPage(Math.min(Math.max(1, p), Math.max(1, lastPage)));

  const getPageButtons = () => {
    const pages = [];
    const maxBtns = 7;

    if (lastPage <= maxBtns) {
      for (let i = 1; i <= lastPage; i++) pages.push(i);
      return pages;
    }

    const windowSize = 1;
    const left = 2;
    const right = 2;

    pages.push(1);
    if (page > left + windowSize + 1) pages.push('left-ellipsis');
    else for (let i = 2; i <= left + 1; i++) pages.push(i);

    const start = Math.max(page - windowSize, left + 2);
    const end = Math.min(page + windowSize, lastPage - right - 1);
    for (let i = start; i <= end; i++) pages.push(i);

    if (page < lastPage - right - windowSize) pages.push('right-ellipsis');
    else for (let i = lastPage - right; i < lastPage; i++) pages.push(i);

    pages.push(lastPage);
    return pages;
  };

  /** =========================
   * render
   * ========================= */
  return (
    <section className="groups-list-page">
      <h2>{t.groupsTitle}</h2>

      {/* Filters */}
      <div className="gl-filters">
        <input
          className="gl-input"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder={t.searchPh}
        />

        <label className="gl-select">
  <span>{locale === 'ru' ? 'Статус' : 'Күйі'}</span>
  <select
    value={statusFilter}
    onChange={(e) => {
      setStatusFilter(e.target.value);
      setPage(1);
    }}
  >
    <option value="all">{locale === 'ru' ? 'Все' : 'Барлығы'}</option>
    <option value="draft">{locale === 'ru' ? 'Не отправлено' : 'Жіберілмеген'}</option>
    <option value="submitted">{locale === 'ru' ? 'Отправлено' : 'Жіберілді'}</option>
    <option value="in_expertise">{locale === 'ru' ? 'На экспертизе' : 'Сараптамада'}</option>
    <option value="draft,submitted">{locale === 'ru' ? 'Черновик + Отправлено' : 'Жоба + Жіберілді'}</option>
  </select>
</label>

        <label className="gl-select">
          <span>{t.sortLabel}</span>
          <select
            value={`${sortBy}:${sortDir}`}
            onChange={(e) => {
              const [sb, sd] = e.target.value.split(':');
              setSortBy(sb);
              setSortDir(sd);
              setPage(1);
            }}
          >
            <option value="created_at:asc">{t.sortCreatedAsc}</option>
            <option value="created_at:desc">{t.sortCreatedDesc}</option>
            <option value="title:asc">{t.sortTitleAsc}</option>
            <option value="title:desc">{t.sortTitleDesc}</option>
          </select>
        </label>

        <label className="gl-select">
          <span>{t.perPage}</span>
          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value) || 10);
              setPage(1);
            }}
          >
            {[10, 20, 30, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>

        <button className="gl-btn gl-btn--ghost" onClick={resetFilters}>
          {t.reset}
        </button>
      </div>

      {loadError && <div className="gl-error">{loadError}</div>}

      {loading && !loadError && (
        <ul className="gl-list">
          {Array.from({ length: 3 }).map((_, i) => (
            <li className="gl-item" key={i}>
              <div className="gl-summary gl-skel">
                <div className="gl-skel__title" />
                <div className="gl-skel__meta" />
              </div>
              <div className="gl-details gl-skel">
                <div className="gl-skel__bar" />
                <div className="gl-skel__chips" />
              </div>
            </li>
          ))}
        </ul>
      )}

      {!loading && !loadError && (
        <>
          <ul className="gl-list">
            {groups.length === 0 && (
              <li className="gl-item">
                <div className="gl-summary">
                  <div className="gl-head">
                    <div className="gl-title">{t.emptyGroups}</div>
                  </div>
                </div>
              </li>
            )}

            {groups.map((group) => {
              const isOpen = String(expandedGroupId) === String(group.id);
              const isEditing = String(editingGroupId) === String(group.id);

              const catsNorm = normalizeCategories(group.categories || []);
              const hasCats = catsNorm.length > 0;


              const { filled, total, label } = countFilled(catsNorm);
              const gKey = statusKey(group.status);

              const canSend = total > 0 && filled === total && gKey === 'draft';
              const hasProcessing = (group.apps || []).some((a) => isOnEditApp(a.status));

              const isNoGroup = String(group.id) === 'no_group';
              const isPreSchool = String(group.education_level_id) === '1';

              return (
                <li key={String(group.id)} className={`gl-item ${isOpen ? 'is-open' : ''}`}>
                  <div className="gl-summary-wrap">
                    {/* Левая часть: только заголовок, кликабельная */}
                    <div
                      className="gl-summary gl-summary--main"
                      role="button"
                      tabIndex={0}
                      onClick={() => setExpanded(isOpen ? null : String(group.id))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpanded(isOpen ? null : String(group.id));
                        }
                      }}
                      aria-expanded={isOpen}
                    >
                      <div className="gl-head">
                        <div className="gl-title">
                          {group.title}
                          {hasProcessing && (
                            <span
                              className="gl-alert"
                              role="img"
                              aria-label={t.hasProcessingTitle}
                              title={t.hasProcessingTitle}
                            >
                              !
                            </span>
                          )}
                        </div>
                        {group.subject && (
                          <div className="gl-subtitle">{byLocaleName(group.subject, locale) || ''}</div>
                        )}
                      </div>
                    </div>

                    {/* Правая часть: мета + кнопка, НЕ кликабельная как аккордеон */}
                    <div className="gl-summary-meta">
                      {hasCats && <span className="gl-pill">{label}</span>}

                      {/* ✅ ОДНА кнопка включает редактирование категорий + общей инфы */}
                      {!isNoGroup && gKey === 'draft' && (
                        <button
                          type="button"
                          className="gl-inline-editcats-btn gl-inline-editcats-btn--compact"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleGroupEdit(group);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onPointerDown={(e) => e.stopPropagation()}
                          title={t.editAllTitle}
                          aria-label={t.editAllTitle}
                        >
                          <span aria-hidden="true">{isEditing ? '✕' : '✎'}</span>
                          <span>{isEditing ? t.closeEdit : t.editAll}</span>
                        </button>
                      )}
                      <span className={`group-status ${groupStatusClass(gKey)}`}>
                        {statusLabel(gKey)}
                      </span>

                      <button
                        type="button"
                        className="gl-chevron-btn"
                        aria-label={
                          isOpen
                            ? locale === 'ru'
                              ? 'Свернуть'
                              : 'Жабу'
                            : locale === 'ru'
                              ? 'Развернуть'
                              : 'Ашу'
                        }
                        onClick={() => setExpanded(isOpen ? null : String(group.id))}
                      >
                        <span className="gl-chevron" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="gl-details gl-details--scroll">
                      <div className="gl-progress">
                        <div className="gl-progress__bar">
                          <span style={{ width: `${total ? (filled / total) * 100 : 0}%` }} />
                        </div>
                        <div className="gl-progress__label">
                          {filled}/{total} {t.categoriesShort}
                        </div>
                      </div>

                      {renderGroupInfo(group)}

                      {catsNorm.some((c) => !c.is_filled) && <p className="gl-warning">{t.catUnfilled}</p>}

                      <GroupCategoriesInlineEditor
                        group={group}
                        locale={locale}
                        t={t}
                        axios={axios}
                        catRefs={catRefs}
                        disabled={gKey !== 'draft' || isNoGroup}
                        isPreSchool={isPreSchool}
                        hideToolbar
                        editSignal={catsEditSignals[String(group.id)] || 0}
                        isEditing={isEditing}
                        onNeedFillCategory={(categoryId) => {
                          setNewAppModal({ open: true, groupId: group.id, categoryId });
                        }}
                        onChanged={async () => {
                          await refreshOneGroup(group.id);
                        }}
                      />

                      {renderAppsTable(group)}

                      {gKey === 'draft' && (
                        <div className="gl-actions">


                          {canSend && (
                            <button className="gl-btn" onClick={() => setModalSendGroup(group)}>
                              {t.sendGroup}
                            </button>
                          )}

                          <button
                            className="gl-btn gl-btn--danger"
                            onClick={() => setModalDeleteGroup(group)}
                            style={{ marginLeft: 8 }}
                          >
                            {t.deleteGroup}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Pagination */}
          <div className="gl-pagination">
            <button className="gl-page-btn" disabled={!canPrev} onClick={() => gotoPage(page - 1)}>
              ← {t.prev}
            </button>

            <div className="gl-page-numbers">
              {getPageButtons().map((p, idx) =>
                p === 'left-ellipsis' || p === 'right-ellipsis' ? (
                  <span key={idx} className="gl-ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    className={`gl-page-btn ${p === page ? 'is-active' : ''}`}
                    onClick={() => gotoPage(p)}
                    disabled={p === page}
                  >
                    {p}
                  </button>
                )
              )}
            </div>

            <button className="gl-page-btn" disabled={!canNext} onClick={() => gotoPage(page + 1)}>
              {t.next} →
            </button>

            <div className="gl-page-info">
              {t.page} {page} {t.of} {lastPage} • {total}
            </div>
          </div>
        </>
      )}

      {/* Confirm send group */}
{modalSendGroup && (
  <div className="modal-backdrop-g">
    <div className="modal-card">
      <h3 className="modal-card-title">{t.confirmSendTitle}</h3>
      <p className="modal-card-message">{t.confirmSendText(modalSendGroup.title)}</p>
      <div className="modal-card-actions">
        <button
          className="btn-filled"
          onClick={() => handleSendGroup(modalSendGroup.id)}
          disabled={sendingGroup}
          style={{ minWidth: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        >
          {sendingGroup ? (
            <>
              <span style={{
                width: 15,
                height: 15,
                border: '2px solid rgba(255,255,255,0.35)',
                borderTopColor: '#fff',
                borderRadius: '50%',
                display: 'inline-block',
                animation: 'gl-spin 0.7s linear infinite',
              }} />
              {locale === 'ru' ? 'Подключение...' : 'Қосылуда...'}
            </>
          ) : (
            t.ecpSign
          )}
        </button>

        <button
          className="btn-flat"
          onClick={() => { setModalSendGroup(null); setSendingGroup(false); }}
          disabled={sendingGroup}
        >
          {t.cancel}
        </button>
      </div>
    </div>
  </div>
)}

      {/* Confirm delete group */}
      {modalDeleteGroup && (
        <div className="modal-backdrop-g">
          <div className="modal-card">
            <h3 className="modal-card-title">{t.confirmDeleteTitle}</h3>
            <p className="modal-card-message">{t.confirmDeleteText(modalDeleteGroup.title)}</p>
            <div className="modal-card-actions">
              <button className="btn-filled btn-danger" onClick={() => handleDeleteGroup(modalDeleteGroup.id)}>
                {t.deleteGroup}
              </button>
              <button className="btn-flat" onClick={() => setModalDeleteGroup(null)}>
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ EDIT GROUP MODAL (separate component) */}
      <EditGroupModal
        open={editGroupOpen}
        groupId={editGroupId}
        locale={locale}
        t={t}
        axios={axios}
        catRefs={catRefs}
        refs={refs}
        refsLoading={refsLoading}
        loadAllRefs={loadAllRefs}
        onClose={closeEditGroup}
        onSaved={fetchAll}
      />

      {/* View / Edit application modal */}
      {/* View / Edit application modal */}
{modal.mode && (
  <div className="ga-modal-backdrop" role="dialog" aria-modal="true">
    <div className="ga-modal-card" onClick={(e) => e.stopPropagation()}>
      <div className="ga-modal-head">
        <h3 className="ga-modal-title">
          {modalSubMode === 'edit'
            ? locale === 'kz' ? 'Өтінімді өңдеу' : 'Редактировать заявку'
            : `${locale === 'kz' ? 'Өтінімді қарау' : 'Просмотр заявки'}`}
        </h3>

        {/* Табы переключения — показываем только если статус позволяет редактировать */}
        {(isDraftApp(modalApp?.status) || isOnEditApp(modalApp?.status)) && (
          <div className="ga-modal-tabs">
            <button
              className={`ga-modal-tab ${modalSubMode === 'view' ? 'is-active' : ''}`}
              onClick={() => setModalSubMode('view')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              {t.view}
            </button>
            <button
              className={`ga-modal-tab ${modalSubMode === 'edit' ? 'is-active' : ''}`}
              onClick={() => setModalSubMode('edit')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              {t.edit}
            </button>
          </div>
        )}

        <button onClick={closeModal} className="ga-modal-close" aria-label="Close">×</button>
      </div>

      <div className="ga-modal-body">
        {modalSubMode === 'edit' ? (
          <PublisherEditInline
            applicationId={modal.applicationId}
            onClose={closeModal}
          />
        ) : (
          <ApplicationViewPublisher
            applicationId={modal.applicationId}
            onClose={closeModal}
            locale={['kk', 'kz'].includes(locale) ? 'kk' : 'ru'}
            embedded={true}
          />
        )}
      </div>
    </div>
  </div>
)}

      {/* NewApplicationForm MODAL */}
      {newAppModal.open && (
        <div className="ga-modal-backdrop" role="dialog" aria-modal="true">
          <div className="ga-modal-card ga-modal-card--xl" onClick={(e) => e.stopPropagation()}>
            <div className="ga-modal-head">
              <h3 className="ga-modal-title">{t.modalFillTitle}</h3>
              <button
                className="ga-modal-close"
                aria-label="Close"
                onClick={() => setNewAppModal({ open: false, groupId: null, categoryId: null })}
              >
                ×
              </button>
            </div>

            <div className="ga-modal-body">
              <NewApplicationForm
                prefill={{ group_id: String(newAppModal.groupId || ''), category_id: String(newAppModal.categoryId || '') }}
                lockPrefill
                onSuccess={async () => {
                  await fetchAll();
                  setNewAppModal({ open: false, groupId: null, categoryId: null });
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add categories modal */}
      {addCatsModal.open && (
        <div className="ga-modal-backdrop" role="dialog" aria-modal="true" onClick={closeAddCats}>
          <div className="ga-modal-card ga-modal-card--xl" onClick={(e) => e.stopPropagation()}>
            <div className="ga-modal-head">
              <h3 className="ga-modal-title">{t.addCatsTitle}</h3>
              <button className="ga-modal-close" aria-label="Close" onClick={closeAddCats}>
                ×
              </button>
            </div>

            <div className="ga-modal-body">
              <p className="gl-addcats-hint">{t.addCatsHint}</p>

              <input
                className="gl-input"
                value={addCatQ}
                onChange={(e) => setAddCatQ(e.target.value)}
                placeholder={t.searchCat}
              />

              {(() => {
                const group = addCatsModal.group;
                const existing = new Set(normalizeCategories(group?.categories || []).map((c) => String(c.id)));

                const list = (catRefs || []).filter((c) => {
                  const name = String(c?.[`name_${locale}`] || c?.name || '').toLowerCase();
                  return !addCatQ || name.includes(addCatQ.toLowerCase());
                });

                if (list.length === 0) return <div className="ngf-empty">{t.addCatsEmpty}</div>;

                return (
                  <div className="gl-addcats-list">
                    {list.map((c) => {
                      const idStr = String(c.id);
                      const picked = addCatPick.includes(idStr);
                      const isExisting = existing.has(idStr);

                      return (
                        <button
                          key={c.id}
                          type="button"
                          className={[
                            'gl-chip',
                            'gl-chip--btn',
                            picked ? 'gl-chip--picked' : '',
                            isExisting ? 'gl-chip--existing' : '',
                          ].join(' ')}
                          onClick={() => togglePickCat(c.id)}
                          title={isExisting ? (locale === 'ru' ? 'Уже в группе' : 'Топта бар') : ''}
                        >
                          {c?.[`name_${locale}`] || c.name}
                          {isExisting ? <span style={{ marginLeft: 8, opacity: 0.7 }}>•</span> : null}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}

              <div className="gl-actions" style={{ marginTop: 12 }}>
                <button className="gl-btn" onClick={saveAddCats} disabled={addCatsSaving}>
                  {addCatsSaving ? '...' : t.addCatsSave}
                </button>
                <button className="gl-btn gl-btn--ghost" onClick={closeAddCats}>
                  {t.addCatsCancel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <NCALayerModal
        open={ncaModalOpen}
        locale={locale}
        onClose={() => {
          setNcaModalOpen(false);
          pendingSendGroupRef.current = null;
        }}
        onRetry={async () => {
          const ok = await ensureNCALayerConnected({ connectTimeoutMs: 1200 });
          if (ok) {
            setNcaModalOpen(false);
            const gid = pendingSendGroupRef.current;
            pendingSendGroupRef.current = null;
            if (gid) handleSendGroup(gid);
          }
        }}
      />
    </section>
  );
}